import { DataTableColumnDef } from "@/components/molecules";
import { DynamicBaseInterval, DynamicInterval, DynamicIntervals } from "@/redux/slices/trip/types";
import { addToast, formatCurrency } from "@/utils";
import { StateRatesFormData } from "./stateRatesFormSchema";
import { PriceRange, StateRatesEditTableData, StateRatesTableData } from "./types";

export const generateColumns = (distanceUnit: string) => {
  const columns: DataTableColumnDef<StateRatesTableData>[] = [
    {
      accessorKey: "rangeIntervals",
      header: "Range Intervals",
      cell: (props) => props.row.original.rangeIntervals,
      size: 250,
      enableSorting: false,
    },
    {
      accessorKey: "range",
      header: `Range (${distanceUnit})`,
      cell: (props) => props.row.original.range,
      enableSorting: false,
    },
    {
      accessorKey: "rate",
      header: `Rate per ${distanceUnit}`,
      cell: (props) => props.row.original.rate,
      size: 200,
      enableSorting: false,
    },
    {
      accessorKey: "price",
      header: "Price",
      cell: (props) => props.row.original.price,
      size: 250,
      enableSorting: false,
    },
  ];

  return columns;
};

export const generateData = (
  baseInterval: DynamicBaseInterval,
  intervals: DynamicIntervals,
  currency: string,
  distanceUnit: string
) => {
  const priceRanges = baseInterval ? getPriceRanges({ baseInterval, intervals }) : [];
  const jointIntervals = baseInterval ? [baseInterval, ...intervals] : [];
  const data = jointIntervals.map((interval, i) => {
    const maximumRange = i < intervals.length ? intervals[i].minimumRange : null;
    return {
      rangeIntervals: i > 0 ? `Range ${i}` : "Minimum Trip Cost",
      range: `${interval.minimumRange}${maximumRange ? ` - ${maximumRange}` : "+"} ${distanceUnit}`,
      rate: i > 0 ? formatCurrency((interval as DynamicInterval).rate, 2, currency, { skipDecimalConversion: true }) : "-",
      price:
        i > 0
          ? `${formatCurrency(priceRanges[i].min, 2, currency, { skipDecimalConversion: true })}${priceRanges[i].max > 0 ? ` - ${formatCurrency(priceRanges[i].max, 2, currency, { skipDecimalConversion: true })}` : "+"}`
          : `${formatCurrency((interval as DynamicBaseInterval)?.cost || 0, 2, currency, { skipDecimalConversion: true })} (Minimum)`,
    } as StateRatesTableData;
  });
  return data;
};

export const generateEditColumns = (distanceUnit: string) => {
  const columns: DataTableColumnDef<StateRatesEditTableData>[] = [
    {
      accessorKey: "rangeIntervals",
      header: "Range Intervals",
      cell: (props) => props.row.original.rangeIntervals,
      size: 250,
      enableSorting: false,
    },
    {
      accessorKey: "range",
      header: `Range (${distanceUnit})`,
      cell: (props) => props.row.original.range,
      enableSorting: false,
    },
    {
      accessorKey: "rate",
      header: `Rate per ${distanceUnit}`,
      cell: (props) => props.row.original.rate,
      size: 200,
      enableSorting: false,
    },
    {
      accessorKey: "price",
      header: "Price",
      cell: (props) => props.row.original.price,
      size: 250,
      enableSorting: false,
    },
    {
      accessorKey: "action",
      header: "",
      cell: (props) => props.row.original.action,
      size: 75,
      enableSorting: false,
    },
  ];

  return columns;
};

export const getPriceRanges = (data: StateRatesFormData): PriceRange[] => {
  const { intervals, baseInterval } = data;
  const baseIntervalCost = { min: 0, max: Number(baseInterval.cost) };

  return intervals.reduce(
    (acc, curr, i) => {
      return [
        ...acc,
        {
          min: acc[i].max,
          max:
            i < intervals.length - 1
              ? acc[i].max + (Number(intervals[i + 1].minimumRange) - Number(curr.minimumRange)) * Number(curr.rate)
              : 0,
        },
      ];
    },
    [baseIntervalCost]
  );
};

export const validateIntervals = (intervals: number[]): boolean => {
  let intervalError = 0;
  const isIncremental = intervals.slice(1).every((item, i) => {
    const condition = intervals[i] < item;
    if (!condition) intervalError = i;

    return condition;
  });

  if (!isIncremental) {
    addToast(
      "danger",
      `Unable to save changes. ${
        intervalError > 0 ? `Range ${intervalError}` : "Minimum Cost"
      } must be incremental over the previous range.`
    );
    return false;
  }

  return true;
};
